// src/pages/PrivacyPage.js
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Database, Eye, Lock, Cookie, Mail } from 'lucide-react';

import Config from "../Config.json";
import ICON from "../assets/icons/favicon.ico";

const TITLE = `${Config.SITE_TITLE} - Política de Privacidade`;

function PrivacyPage() {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const Section = ({ icon: Icon, title, children }) => (
        <div className="mb-10">
            <h2 className="font-serif text-2xl text-[#528a89] flex items-center gap-3 mb-4 border-b border-[#dbcdac]/30 pb-2">
                <Icon size={24} className="text-[#dbcdac]" /> {title}
            </h2>
            <div className="text-stone-600 space-y-4 font-light leading-relaxed">
                {children}
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-[#FFF5EE] pt-24 pb-32 relative overflow-hidden font-sans text-stone-800">
            <title>{TITLE}</title>
            <link rel="icon" href={ICON} />

            {/* Textura Vintage */}
            <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/aged-paper.png')]" />

            <div className="max-w-4xl mx-auto px-4 md:px-6 relative z-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
                
                <button 
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-stone-400 hover:text-[#971253] font-bold uppercase tracking-widest text-xs mb-8 transition-colors group"
                >
                    <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" /> 
                    Voltar
                </button>

                <div className="text-center md:text-left mb-12">
                    <h1 className="font-serif text-4xl md:text-5xl font-bold text-[#971253] mb-4">
                        Política de Privacidade
                    </h1>
                    <p className="text-lg text-[#528a89] font-light">
                        Como protegemos os segredos e as informações do nosso conclave.
                    </p>
                    <p className="text-xs text-stone-400 uppercase tracking-widest mt-4">
                        Última atualização: Fevereiro de 2026
                    </p>
                </div>

                <div className="bg-white/80 backdrop-blur-sm rounded-[2rem] border border-[#dbcdac]/40 shadow-sm p-8 md:p-12">
                    
                    <Section icon={Database} title="1. Informações que Coletamos">
                        <p>
                            Quando você interage com a <strong>{Config.SITE_TITLE}</strong>, coletamos apenas o estritamente necessário para garantir que sua experiência seja perfeita:
                        </p>
                        <ul className="list-disc pl-5 space-y-2 text-stone-600">
                            <li><strong>Dados de Autenticação:</strong> Ao fazer login via Google, recebemos seu nome, e-mail e foto de perfil.</li>
                            <li><strong>Dados de Contato:</strong> Número de telefone (WhatsApp) fornecido no seu perfil ou no momento da reserva.</li>
                            <li><strong>Dados de Uso e Reservas:</strong> Seu histórico de mesas jogadas, sistemas de RPG escolhidos, participantes das suas mesas e status de assinaturas.</li>
                            <li><strong>Dados Financeiros:</strong> Histórico de pagamentos e divisão de contas (Split). <em>Nota: Não armazenamos o número do seu cartão de crédito; isso é feito de forma segura pelos nossos parceiros de pagamento.</em></li>
                        </ul>
                    </Section>

                    <Section icon={Eye} title="2. Como Usamos Seus Dados">
                        <p>
                            Suas informações são a chave para organizarmos sua jogatina. Nós as utilizamos para:
                        </p>
                        <ul className="list-disc pl-5 space-y-2 text-stone-600">
                            <li>Criar e gerenciar suas reservas de mesas e assinaturas do Clube.</li>
                            <li>Processar a divisão de contas (Split Payment) enviando os links corretos para cada participante da sua Party.</li>
                            <li>Enviar confirmações, alertas de tempo limite de pagamento e atualizações via WhatsApp ou E-mail.</li>
                            <li>Personalizar sua experiência no salão e recomendar eventos ou mesas que combinem com o seu histórico.</li>
                        </ul>
                    </Section>

                    <Section icon={ShieldCheck} title="3. Compartilhamento de Dados">
                        <p>
                            A Deli da Persy <strong>jamais venderá ou comercializará</strong> seus dados pessoais com terceiros. O compartilhamento ocorre apenas nas seguintes situações essenciais:
                        </p>
                        <ul className="list-disc pl-5 space-y-2 text-stone-600">
                            <li><strong>Provedores de Serviço:</strong> Empresas de tecnologia que hospedam nosso sistema (como AWS) e plataformas de pagamento que processam suas transações com segurança militar.</li>
                            <li><strong>Requisitos Legais:</strong> Se formos obrigados por lei, ordem judicial ou regulamentação governamental.</li>
                        </ul>
                    </Section>

                    <Section icon={Cookie} title="4. Cookies e Tecnologias">
                        <p>
                            Utilizamos "tokens" de sessão (armazenados de forma segura no seu navegador) exclusivamente para manter você logado(a) no sistema enquanto navega, evitando que você precise fazer o login do Google repetidas vezes. Não utilizamos cookies invasivos de rastreamento de navegação em outros sites.
                        </p>
                    </Section>

                    <Section icon={Lock} title="5. Segurança da Informação">
                        <p>
                            Tratamos a segurança dos seus dados como a defesa de uma fortaleza. Utilizamos criptografia e protocolos de segurança modernos para proteger suas informações contra acesso não autorizado, alteração, divulgação ou destruição. Nosso banco de dados é restrito e acessível apenas por pessoal autorizado e sujeito a obrigações de confidencialidade.
                        </p>
                    </Section>

                    <Section icon={Mail} title="6. Seus Direitos (LGPD)">
                        <p>
                            De acordo com a Lei Geral de Proteção de Dados, você tem o controle total sobre a sua "ficha de personagem" na vida real. Você pode nos solicitar a qualquer momento:
                        </p>
                        <ul className="list-disc pl-5 space-y-2 text-stone-600">
                            <li>Confirmação de que tratamos seus dados e acesso a eles.</li>
                            <li>Correção de dados incompletos, inexatos ou desatualizados.</li>
                            <li>Exclusão completa da sua conta e anonimização do seu histórico (salvo o que for exigido manter por obrigações fiscais e legais).</li>
                        </ul>
                        <p className="pt-2">
                            Para exercer esses direitos, basta enviar uma mensagem aos nossos canais oficiais de atendimento.
                        </p>
                    </Section>

                </div>
            </div>
        </div>
    );
}

export default PrivacyPage;